import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppStore } from '../contexts/store';
import {
  getAppSection,
  setAnalyticsContext,
  trackPageView,
} from '../utils/analytics';

/**
 * Drives Google Analytics page-view tracking for the React Router SPA.
 *
 * Mount once near the top of the React tree (inside `BrowserRouter` so
 * `useLocation` works). On every path change it:
 *   - Refreshes the ambient analytics context (user-id, role, tenant,
 *     section) so subsequent custom events get tagged consistently.
 *   - Fires a `page_view` event. The GA snippet in `index.html` sets
 *     `send_page_view: false` so this is the only place pageviews fire —
 *     no double-counting on the initial load.
 *
 * Why a hook (vs. a side-effect at the gtag level): React Router's
 * `useLocation` is the canonical source of truth for the current SPA path.
 * Watching `history.pushState` directly is fragile and misses replace
 * navigations.
 */
export function useAnalyticsPageView(): void {
  const location = useLocation();
  const user = useAppStore((s) => s.user);
  const tenantConfig = useAppStore((s) => s.tenantConfig);

  useEffect(() => {
    const section = getAppSection(location.pathname);

    setAnalyticsContext({
      userId: user?.id,
      userRole: user?.roleName ?? user?.role ?? undefined,
      tenantSubdomain: tenantConfig?.subdomain,
      appSection: section,
    });

    trackPageView(location.pathname);
  }, [
    location.pathname,
    user?.id,
    user?.role,
    user?.roleName,
    tenantConfig?.subdomain,
  ]);
}
